import { computed, defineComponent, getCurrentInstance, onMounted, ref, useContext, useRoute, watch, } from '@nuxtjs/composition-api';
import { useTask } from 'vue-concurrency';
import { SPECIAL_SECTIONS } from '~/constants';
import { cachePage, createHead, getPubSpecial, sendAnalytics, useAsyncRef } from '~/utils';
import { createGridChunks, getArticlesSize } from '~/composables';
import { presets } from '~/composables/grid/ratings';
import { useDuck } from '~/services';
const DEFAULT_TYPE = 'all';
/**
 * Duck service.
 */
const duck = useDuck();
/**
 * Columns
 */
const columns = {
    desktop: 2,
    desktopMedium: 2,
    desktopWide: 3,
    mobile: 1,
    mobileMedium: 1,
    mobileWide: 1,
    tablet: 2,
};
export default defineComponent({
    name: 'PageSpecial',
    setup() {
        const context = useContext();
        const route = useRoute();
        const viewport = context.$viewport;
        /**
         * Instance.
         */
        const instance = getCurrentInstance();
        /**
         * Is done.
         */
        const isDone = useAsyncRef(instance, 'isDone');
        /**
         * Articles.
         */
        const articles = useAsyncRef(instance, 'articles');
        /**
         * Current type.
         */
        const currentType = useAsyncRef(instance, 'currentType');
        const defaultTitle = useAsyncRef(instance, 'defaultTitle');
        const dropdownItems = useAsyncRef(instance, 'dropdownItems');
        const tabs = computed(() => {
            const tabs = [...dropdownItems.value];
            return tabs.map((item) => {
                item.theme = 'white';
                if (item.value === currentType.value) {
                    item.theme = 'light';
                }
                return item;
            });
        });
        const currentTab = computed(() => {
            return tabs.value.find(({ value }) => value === currentType.value);
        });
        const searchQuery = ref('');
        const timeout = ref();
        const referer = ref('');
        /**
         * Current preset.
         */
        const articlesCards = computed(() => getArticlesSize(viewport.breakpoint, {
            columns,
            presets,
        }));
        /**
         * Grids.
         */
        const grids = computed(() => createGridChunks(articles.value, articlesCards.value));
        const pageOffset = useAsyncRef(instance, 'pageOffset');
        /**
         * Fetch articles task.
         */
        const fetchArticlesTask = useTask(function* (signal, shouldEmpty = false) {
            signal.addEventListener('abort', () => duck.abortRequest('fetchArticlesTask'));
            if (searchQuery.value) {
                articles.value = [];
                isDone.value = true;
                return;
            }
            try {
                if (shouldEmpty) {
                    articles.value = [];
                    isDone.value = false;
                }
                const { data = [] } = yield getPubSpecial({
                    duck,
                    type: currentType.value,
                }, {
                    'list[limit]': articlesCards.value,
                    'list[offset]': shouldEmpty ? 0 : (pageOffset.value + 1) * articles.value.length,
                }, {
                    cancelToken: 'fetchArticlesTask',
                });
                if (data.length < articlesCards.value) {
                    isDone.value = true;
                }
                if (!data.length) {
                    return;
                }
                articles.value = shouldEmpty ? data : [...articles.value, ...data];
            }
            catch (error) {
                console.error(error);
                isDone.value = true;
            }
        });
        /**
         * Search articles task.
         */
        const searchTask = useTask(function* (signal, query) {
            signal.addEventListener('abort', () => duck.abortRequest('searchTask'));
            isDone.value = false;
            try {
                articles.value = [];
                const { data } = yield getPubSpecial({
                    duck,
                    type: currentType.value,
                }, {
                    'node[title]': query,
                }, {
                    cancelToken: 'searchTask',
                });
                articles.value = [...articles.value, ...data];
            }
            catch (error) {
                console.error(error);
            }
            finally {
                isDone.value = true;
            }
        });
        function handleDropdownInput(value) {
            switchSection(value);
        }
        function handleIntersect() {
            if (fetchArticlesTask.isRunning || isDone.value) {
                return;
            }
            fetchArticlesTask.perform();
        }
        function handleGridIntersect(gridKey) {
            gridKey = gridKey + 1 + pageOffset.value;
            if (currentType.value !== DEFAULT_TYPE) {
                gridKey = 0;
                pageOffset.value = 0;
            }
            const params = new URLSearchParams(location.search);
            if (gridKey > 1) {
                params.set('page', gridKey.toString());
            }
            else {
                params.delete('page');
            }
            const query = params.toString();
            history.replaceState(null, '', query.length ? `?${query}` : location.pathname);
        }
        function handleTabsChange({ value }) {
            switchSection(value);
        }
        function handleSearchInput(event) {
            clearTimeout(timeout.value);
            timeout.value = window.setTimeout(() => {
                const { value } = event.target;
                searchQuery.value = value;
                if (value === '') {
                    fetchArticlesTask.perform(true);
                }
                else {
                    searchTask.perform(value);
                }
            }, 500);
        }
        function handleSearchReset() {
            searchQuery.value = '';
            fetchArticlesTask.perform(true);
        }
        function switchSection(type) {
            currentType.value = type;
            searchQuery.value = '';
            let currentName;
            const params = new URLSearchParams();
            if (currentType.value === DEFAULT_TYPE) {
                currentName = '';
                params.delete('type');
            }
            else {
                const index = dropdownItems.value.findIndex(({ value }) => currentType.value === value);
                currentName = `${dropdownItems.value[index].title} – `;
                params.set('type', currentType.value);
            }
            const query = params.toString();
            history.replaceState(null, '', query.length ? `?${query}` : location.pathname);
            document.title = currentName + `${defaultTitle.value} | Forbes.ru`;
            fetchArticlesTask.perform(true);
            sendAnalytics.call(context, {
                path: location.pathname,
                referer: referer.value,
                title: document.title,
                url: location.href,
            });
            // context.$ioAnalytics({
            //   page_language: 'ru',
            //   page_title: document.title,
            //   page_type: 'default',
            //   page_url: location.href,
            //   page_url_canonical: context.env.HOST + location.pathname,
            // })
            referer.value = location.href;
        }
        onMounted(() => {
            watch(route, ({ query: { type = '' } }) => {
                if (!type) {
                    return;
                }
                switchSection(type);
            });
        });
        return {
            articles,
            articlesCards,
            columns,
            currentTab,
            currentType,
            dropdownItems,
            fetchArticlesTask,
            grids,
            handleDropdownInput,
            handleGridIntersect,
            handleIntersect,
            handleSearchInput,
            handleSearchReset,
            handleTabsChange,
            isDone,
            pageOffset,
            presets,
            searchTask,
            searchQuery,
            tabs,
        };
    },
    async asyncData(context) {
        const { app, env, error, req, res, route } = context;
        try {
            let currentType = DEFAULT_TYPE;
            let isDone = false;
            const defaultDescription = `Спецпроекты – Forbes Russia – одно из наиболее авторитетных и известных финансово-экономических изданий в мире`;
            const defaultTitle = 'Спецпроекты';
            const dropdownItems = [
                {
                    title: 'Все проекты',
                    value: DEFAULT_TYPE,
                },
                ...Object.entries(SPECIAL_SECTIONS).map(([key, { title }]) => {
                    return {
                        title,
                        value: key,
                    };
                }),
                {
                    title: 'Лендинги',
                    value: 'landings',
                },
            ];
            const currentDropdownIndex = dropdownItems.findIndex(({ value }) => route.query.type === value);
            if (currentDropdownIndex !== -1) {
                currentType = String(route.query.type);
            }
            const limit = getArticlesSize(app.$viewport.breakpoint, {
                columns,
                presets,
            });
            const pageNumber = Number(route.query.page) || 0;
            const pageOffset = pageNumber > 0 ? pageNumber - 1 : 0;
            const { data = [] } = await getPubSpecial({
                duck,
                type: currentType,
            }, {
                'list[limit]': limit,
                'list[offset]': limit * pageOffset,
            });
            if (pageNumber && !data?.length) {
                throw new Error('Not found');
            }
            if (data.length < limit) {
                isDone = true;
            }
            let canonicalTail = '';
            let currentName = '';
            if (![DEFAULT_TYPE, 'brandvoice'].includes(currentType)) {
                canonicalTail = `?type=${currentType}`;
                currentName = `${dropdownItems[currentDropdownIndex].title} – `;
            }
            const title = currentName + defaultTitle;
            const head = {
                canonical: env.HOST + route.path + canonicalTail,
                description: currentName + defaultDescription,
                title,
                url: env.HOST + route.fullPath,
            };
            context.$page = app.$page = {
                title,
            };
            if (process.server) {
                const [firstArticle] = data;
                if (firstArticle) {
                    const date = firstArticle.changed || firstArticle.time || firstArticle.created;
                    cachePage({ date, req, res });
                }
            }
            return {
                $page: context.$page,
                articles: data,
                currentType,
                defaultTitle,
                dropdownItems,
                head,
                isDone,
                pageOffset,
                presets,
            };
        }
        catch (e) {
            console.error(e);
            error({ statusCode: 404 });
        }
    },
    head() {
        return createHead(this.head);
    },
});
